// New-order chime: two WebAudio notes, no audio file. Off until the user turns it on (WCAG 1.4.2).
// An mp3 can replace playChime() later without touching callers.
import { useSyncExternalStore } from 'react';

export const SOUND_KEY = 'qo.orders.sound';

export interface SoundState {
    on: boolean;
    /** Sound is on from an earlier session but the browser has not yet allowed playback. */
    blocked: boolean;
}

const NOTES = [
    { hz: 880, at: 0 },
    { hz: 1318.5, at: 0.16 },
];
const GESTURES = ['pointerdown', 'keydown', 'touchend'] as const;

let ctx: AudioContext | null = null;
let state: SoundState = { on: localStorage.getItem(SOUND_KEY) === 'on', blocked: false };
const listeners = new Set<() => void>();

const set = (next: SoundState) => {
    state = next;
    listeners.forEach((l) => l());
};

const getContext = (): AudioContext | null => {
    if (!ctx && typeof AudioContext !== 'undefined') ctx = new AudioContext();
    return ctx;
};

/** Create or resume the audio context. Must run inside a user gesture the first time. */
const unlock = async (): Promise<boolean> => {
    const c = getContext();
    if (!c) return false;
    if (c.state !== 'running') await c.resume().catch(() => undefined);
    return c.state === 'running';
};

const onGesture = () => {
    void unlock().then((ok) => {
        if (ok) {
            GESTURES.forEach((g) => window.removeEventListener(g, onGesture, true));
            set({ ...state, blocked: false });
        }
    });
};

const waitForGesture = () => {
    GESTURES.forEach((g) => window.addEventListener(g, onGesture, true));
};

const ring = (c: AudioContext) => {
    const now = c.currentTime;
    NOTES.forEach(({ hz, at }) => {
        const osc = c.createOscillator();
        const gain = c.createGain();
        osc.type = 'sine';
        osc.frequency.value = hz;
        gain.gain.setValueAtTime(0.0001, now + at);
        gain.gain.exponentialRampToValueAtTime(0.25, now + at + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + at + 0.3);
        osc.connect(gain).connect(c.destination);
        osc.start(now + at);
        osc.stop(now + at + 0.32);
    });
};

/** Plays the chime if sound is on and the browser allows it. Returns whether it played. */
export const playChime = async (): Promise<boolean> => {
    if (!state.on) return false;
    const c = getContext();
    if (!c || c.state !== 'running') {
        if (!state.blocked) {
            set({ ...state, blocked: true });
            waitForGesture();
        }
        return false;
    }
    ring(c);
    return true;
};

/** Call from a click handler: the gesture unlocks audio and the chime confirms it. */
export const setSoundOn = async (on: boolean): Promise<void> => {
    localStorage.setItem(SOUND_KEY, on ? 'on' : 'off');
    set({ on, blocked: false });
    const c = getContext();
    if (on && c && (await unlock())) ring(c);
};

/** Banner action: a tap on the banner is itself the gesture that unlocks audio. */
export const unlockSound = async (): Promise<void> => {
    if (await unlock()) set({ ...state, blocked: false });
};

// Sound left on from an earlier session: audio stays blocked until the first gesture.
if (state.on) {
    const c = getContext();
    if (c && c.state !== 'running') {
        state = { on: true, blocked: true };
        waitForGesture();
    }
}

const subscribe = (l: () => void) => {
    listeners.add(l);
    return () => listeners.delete(l);
};

export const useSound = (): SoundState => useSyncExternalStore(subscribe, () => state);
